/**
 * EditMemoryModal - Modal for editing existing memories in Knowledge Base
 * Supports category selection and importance adjustment
 */

import { useState, useEffect } from "react";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import MicButton from "./MicButton";
import "../../assets/css/sb-modal.css";

// Category configuration with icons and colors
const CATEGORY_CONFIG = {
  identity: { icon: "👤", label: "Identity", color: "#6366f1" },
  preference: { icon: "❤️", label: "Preference", color: "#f97316" },
  personality: { icon: "🎭", label: "Personality", color: "#8b5cf6" },
  health: { icon: "🏥", label: "Health", color: "#ef4444" },
  finance: { icon: "💰", label: "Finance", color: "#22c55e" },
  work: { icon: "💼", label: "Work", color: "#3b82f6" },
  education: { icon: "🎓", label: "Education", color: "#0ea5e9" },
  relationship: { icon: "👨‍👩‍👧", label: "Relationship", color: "#ec4899" },
  food: { icon: "🍽️", label: "Food", color: "#14b8a6" },
  travel: { icon: "✈️", label: "Travel", color: "#06b6d4" },
  hobby: { icon: "🎮", label: "Hobby", color: "#a855f7" },
  schedule: { icon: "📅", label: "Schedule", color: "#8b5cf6" },
  goal: { icon: "🎯", label: "Goal", color: "#eab308" },
  event: { icon: "📆", label: "Event", color: "#f59e0b" },
  general: { icon: "📝", label: "General", color: "#6b7280" },
};

export default function EditMemoryModal({ show, onClose, memory, onSave }) {
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [importance, setImportance] = useState(0.5);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Initialize form when memory changes
  useEffect(() => {
    if (memory) {
      setContent(memory.memory || "");
      setCategory(memory.category || "general");
      setImportance(memory.importance || 0.5);
    }
  }, [memory]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      setError("Please enter memory content");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave(memory.id, content.trim(), category, importance);
      handleClose();
    } catch (err) {
      setError("Failed to update memory");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setShowPreview(false);
    onClose();
  };

  const getImportanceLabel = (value) => {
    if (value >= 0.9) return "Critical";
    if (value >= 0.7) return "High";
    if (value >= 0.5) return "Medium";
    return "Low";
  };

  const getImportanceColor = (value) => {
    if (value >= 0.9) return "#ef4444";
    if (value >= 0.7) return "#f97316";
    if (value >= 0.5) return "#eab308";
    return "#6b7280";
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="sb-modal">
      <Modal.Header closeButton className="sb-modal-header">
        <Modal.Title className="sb-modal-title">
          <i className="bi bi-pencil-fill me-2"></i>
          Edit Memory
        </Modal.Title>
      </Modal.Header>
      
      <form onSubmit={handleSubmit}>
        <Modal.Body className="sb-modal-body">
          {error && (
            <div className="sb-error-alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          {/* Content with Preview Toggle */}
          <div className="sb-form-group">
            <div className="sb-label-row">
              <label className="sb-label">Memory Content <span className="required">*</span></label>
              <button
                type="button"
                className={`sb-preview-toggle ${showPreview ? 'active' : ''}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                <i className={`bi ${showPreview ? 'bi-pencil' : 'bi-eye'} me-1`}></i>
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            </div>
            
            {showPreview ? (
              <div className="sb-content-preview">
                {content ? (
                  <ReactMarkdown>{content}</ReactMarkdown>
                ) : (
                  <span className="sb-preview-empty">Nothing to preview</span>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <textarea
                  className="sb-textarea"
                  rows={5}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter memory content..."
                  disabled={saving}
                  autoFocus
                  style={{ flex: 1 }}
                />
                <MicButton
                  onTranscript={(text) => setContent(prev => prev ? `${prev} ${text}` : text)}
                  disabled={saving}
                  size="md"
                />
              </div>
            )}
          </div>

          {/* Category Selection */}
          <div className="sb-form-group">
            <label className="sb-label">Category</label>
            <Form.Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="kb-category-select"
              disabled={saving}
            >
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.icon} {config.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Importance Slider */}
          <div className="sb-form-group">
            <label className="sb-label">
              Importance: 
              <span 
                className="kb-importance-badge"
                style={{ backgroundColor: getImportanceColor(importance), marginLeft: '8px' }}
              >
                {getImportanceLabel(importance)} ({Math.round(importance * 100)}%)
              </span>
            </label>
            <input
              type="range"
              className="kb-importance-slider"
              min="0"
              max="1"
              step="0.05"
              value={importance}
              onChange={(e) => setImportance(parseFloat(e.target.value))}
              disabled={saving}
            />
            <div className="kb-importance-labels">
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
              <span>Critical</span>
            </div>
          </div>

          {/* Tags Display (read-only for now) */}
          {memory?.tags && memory.tags.length > 0 && (
            <div className="sb-form-group">
              <label className="sb-label">Tags</label>
              <div className="kb-tags-display">
                {memory.tags.map((tag, i) => (
                  <span key={i} className="kb-tag">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="sb-modal-footer">
          <Button 
            variant="secondary" 
            onClick={handleClose} 
            disabled={saving}
            className="sb-btn-cancel"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={saving || !content.trim()}
            className="sb-btn-save"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
